import React, { useEffect, useState } from "react";
import { DatePicker, Form, Input, Select, TimePicker } from "antd";
import FormItem from "components/fields/FormItem";
import { useDispatch, useSelector } from "react-redux";
import Card from "components/card";
import {
  getAnnouncementDetails,
  doAnnouncementCreate,
  doAnnouncementUpdate,
} from "../../../../redux/announcement/announcementState";
import { getClientList } from "../../../../redux/optionlist/optionlistState";
import CrudButtons from "components/button/crudbuttons";
import { requiredField } from "utils/crudSagaHelper";
import { ANNOUNCEMENT_COLOR_OPTIONS, DATE_FORMAT, TIME_FORMAT_12HM } from "../../../../variables/constants";
import { displayIcon, USABLE_ICONS } from "variables/icons";
import { MODULE_ANNOUNCEMENTS } from "variables/modules";
import dayjs from "dayjs";
import SwitchField from "components/fields/SwitchField";

const AnnouncementForm = ({
  onCancel,
  submitLabel,
  ...props
}) => {
  const [form] = Form.useForm();
  const { RangePicker } = TimePicker;
  const { recordId, mode } = props;
  const isEditMode = mode === "edit";

  const dispatch = useDispatch();

  const recordData = useSelector((state) => state.announcement.announcementDetails?.data);
  const clientList = useSelector((state) => state.optionlist.clientList?.items || []);
  const announcementCategoryList = recordData?.announcementCategoryList ?? [];
  const announcementPriorityList = recordData?.announcementPriorityList ?? [];
  
  const doCreate = (data) => dispatch(doAnnouncementCreate(data));
  const doUpdate = (data) => dispatch(doAnnouncementUpdate(data));

  const selectedViewType = Form.useWatch("ann_view_type_id", form);
  const [eventDateToPickerValue, setEventDateToPickerValue] = useState(dayjs());
  const [periodDateToPickerValue, setPeriodDateToPickerValue] = useState(dayjs());
  
  useEffect(() => {
      dispatch(getAnnouncementDetails(recordId));
  }, [dispatch, recordId]);

  useEffect(() => {
    dispatch(getClientList());
  }, [dispatch]);

  useEffect(() => {
    if (recordData) {
      form.resetFields();

      const initialValues = {
        ...recordData,
        ann_client_ids: recordData?.ann_client_ids ?? [],
        ann_period_from: recordData?.ann_period_from
          ? dayjs(recordData.ann_period_from)
          : null,
        ann_period_to: recordData?.ann_period_to
          ? dayjs(recordData.ann_period_to)
          : null,
        ann_event_date_from: recordData?.ann_event_date_from
          ? dayjs(recordData.ann_event_date_from)
          : null,
        ann_event_date_to: recordData?.ann_event_date_to
          ? dayjs(recordData.ann_event_date_to)
          : null,
        time_range:
          recordData?.ann_event_time_from && recordData?.ann_event_time_to
            ? [
                dayjs(recordData.ann_event_time_from, ["HH:mm:ss", "HH:mm"]),
                dayjs(recordData.ann_event_time_to, ["HH:mm:ss", "HH:mm"]),
              ]
            : null,
      };
      form.setFieldsValue(initialValues);
    }
  }, [recordData, form]);

  const onSubmit = (formData) => {
    try {
      const submitData = {
        ...formData,

        ann_period_from: formData.ann_period_from
          ? formData.ann_period_from.format("YYYY-MM-DD")
          : null,
        ann_period_to: formData.ann_period_to
          ? formData.ann_period_to.format("YYYY-MM-DD")
          : null,
        ann_event_date_from: formData.ann_event_date_from
          ? formData.ann_event_date_from.format("YYYY-MM-DD")
          : null,
        ann_event_date_to: formData.ann_event_date_to
          ? formData.ann_event_date_to.format("YYYY-MM-DD")
          : null,
        ann_event_time_from: formData.time_range?.[0]
          ? formData.time_range[0].format("HH:mm")
          : null,
        ann_event_time_to: formData.time_range?.[1]
          ? formData.time_range[1].format("HH:mm")
          : null,
        ann_to_popup: formData.ann_to_popup ? 1 : 0,
    };

      delete submitData.time_range;

      recordData?.ann_id > 0
        ? doUpdate(submitData)
        : doCreate(submitData);
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  return (<>
    <Card extra="p-4 dark-mode-card rounded-xl border-2">
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        className="form"
      >
        <FormItem name="ann_id" hidden>
          <Input type="hidden" />
        </FormItem>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-2 w-full">
            <FormItem
              name="ann_title"
              label="Announcement Title"
              rules={[requiredField]}
              className="col-span-2"
              value={recordData?.ann_title}
            >
              {isEditMode && (
                <Input
                  id="ann_title"
                  placeholder="Enter announcement title"
                  className="input-field"
                  maxLength={100}
                />
              )}
            </FormItem>

            <FormItem
              name="ann_icon"
              label="Icon"
              rules={[requiredField]}
              className="col-span-1"
              value={
                <span className="flex items-center gap-1">
                  {displayIcon(recordData?.ann_icon, "font-medium w-5 h-5")}
                  {recordData?.ann_icon}
                </span>}
            >
              {isEditMode && (
                <Select
                  placeholder="Select icon"
                  className="input-field"
                  showSearch
                  allowClear
                  options={Object.keys(USABLE_ICONS).map((key) => ({
                    label: (
                      <div className="flex items-center gap-2">
                        {displayIcon(key, "w-4 h-4 muted-text")}
                        <span>{key}</span>
                    </div>
                  ),
                  value: key,
                }))}
              />
              )}
            </FormItem>

            <FormItem
              name="ann_description"
              label="Description"
              rules={[requiredField]}
              className="col-span-3"
              value={recordData?.ann_description}
            >
              {isEditMode && (
                <Input.TextArea
                  placeholder="Enter description"
                  className="input-field"
                  maxLength={500}
                  rows={2}
                />
              )}
            </FormItem>

            <FormItem
              name="ann_category"
              label="Category"
              rules={[requiredField]}
              className="col-span-1"
              value={recordData?.ann_category}
            >
              {isEditMode && (
              <Select
                id="ann_category"
                placeholder="Select category"
                className="input-field"
                options={announcementCategoryList}
                showSearch
                allowClear
              />
              )}
            </FormItem>

            <FormItem
              name="ann_color_class"
              label="Color"
              rules={[requiredField]}
              className="col-span-1"
              value={
                ANNOUNCEMENT_COLOR_OPTIONS.find(
                  (color) => color.value === recordData?.ann_color_class
                )?.label
              }
            >
              {isEditMode && (
                <Select
                  placeholder="Select color"
                  className="input-field"
                  options={ANNOUNCEMENT_COLOR_OPTIONS}
                />
              )}
            </FormItem>

            <FormItem
              name="ann_priority_level"
              label="Priority"
              className="col-span-1"
              value={recordData?.priority_level || "-"}
            >
              {isEditMode && (
                <Select
                  placeholder="Select priority"
                  className="input-field"
                  showSearch
                  allowClear
                  options={announcementPriorityList}
                />
              )}
            </FormItem>
            
            <FormItem
              label="Display From"
              name="ann_period_from"
              rules={[requiredField()]}
              value={recordData?.ann_period_from}
            >
              {isEditMode && (
              <DatePicker
                id="ann_period_from"
                className="input-field"
                format={DATE_FORMAT}
                style={{ width: "100%" }}
                onChange={(date) => {
                  if (date) {
                    setPeriodDateToPickerValue(date);
                  }
                }}
              />
              )}
            </FormItem>

            <FormItem
              label="Display Until"
              name="ann_period_to"
              dependencies={["ann_period_from"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const fromDate = getFieldValue(
                      "ann_period_from"
                    );

                    if (!value || !fromDate) {
                      return Promise.resolve();
                    }

                    if (value.isBefore(fromDate, "day")) {
                      return Promise.reject(
                        new Error(
                          "Dispay Date 'To' cannot be earlier than 'From'"
                        )
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              value={recordData?.ann_period_to || "No End Date"}
            >
              {isEditMode && (
                <DatePicker
                  id="ann_period_to"
                  className="input-field"
                  format={DATE_FORMAT}
                  style={{ width: "100%" }}
                  pickerValue={periodDateToPickerValue}
                onPanelChange={(value) => {
                  setPeriodDateToPickerValue(value);
                }}
                disabledDate={(current) => {
                  const fromDate = form.getFieldValue("ann_period_from");

                  return fromDate && current.isBefore(fromDate, "day");
                }}
              />
              )}
            </FormItem>

            <FormItem
              name="ann_view_type_id"
              label="Target Viewer"
              rules={[requiredField]}
              className="col-span-1"
              value={
                {
                  0: "All",
                  1: "Client",
                  2: "Employee",
                }[recordData?.ann_view_type_id]
              }
            >
              {isEditMode && (
                <Select
                  placeholder="Select audience"
                  className="input-field"
                  options={[
                    { label: "All", value: 0 },
                    { label: "Client", value: 1 },
                    { label: "Employee", value: 2 },
                  ]}
                />
              )}
            </FormItem>
            
            <div className="-space-y-1 col-span-3 mb-5">
              <FormItem
                name="ann_client_ids"
                label="Specific Clients"
                value={
                  recordData?.ann_client_ids?.length
                    ? recordData.ann_client_ids
                        .map(
                          (id) =>
                            clientList.find(
                              (client) => client.value === id
                            )?.label ?? id
                        )
                        .join(", ")
                    : recordData?.ann_view_type_id === 1
                    ? "All Clients"
                    : recordData?.ann_view_type_id === 2
                    ? "Employees from All Clients"
                    : "-"
                }
              >
                {isEditMode && (
                <Select
                    id="ann_client_ids"
                    placeholder="Select Clients"
                    mode="multiple"
                    className="input-field !p-0"
                    options={clientList}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                />
                )}
              </FormItem>  
              {isEditMode && (
              <p className="text-xs muted-text">
                  {selectedViewType === 0
                      ? "Leave empty to target all clients and employees."
                      : selectedViewType === 1
                          ? "Leave empty to target all clients."
                          : "Leave empty to target employees from all clients."}
              </p>  
              )}
            </div>

            <FormItem
              label="Event Date From"
              name="ann_event_date_from"
              className="col-span-1"
              rules={[
                ({ getFieldValue }) => {
                  const toDate = getFieldValue("ann_event_date_to");

                  return {
                    ...requiredField(),
                    required: !!toDate,
                  };
                },
              ]}
              value={recordData?.ann_event_date_from || "-"}
            >
              {isEditMode && (
                <DatePicker
                  id="ann_event_date_from"
                  className="input-field"
                  format={DATE_FORMAT}
                  style={{ width: "100%" }}
                  onChange={(date) => {
                  if (date) {
                    setEventDateToPickerValue(date);
                  }
                }}
              />
              )}
            </FormItem>

            <FormItem
              label="Event Date To"
              name="ann_event_date_to"
              dependencies={["ann_event_date_from"]}
              className="col-span-1"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const fromDate = getFieldValue(
                      "ann_event_date_from"
                    );

                    if (!value || !fromDate) {
                      return Promise.resolve();
                    }

                    if (value.isBefore(fromDate, "day")) {
                      return Promise.reject(
                        new Error(
                          "Date 'To' cannot be earlier than 'From'"
                        )
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              value={recordData?.ann_event_date_to || "-"}
            >
              {isEditMode && (
                <DatePicker
                  id="ann_event_date_to"
                  className="input-field"
                  format={DATE_FORMAT}
                  style={{ width: "100%" }}
                  pickerValue={eventDateToPickerValue}
                onPanelChange={(value) => {
                  setEventDateToPickerValue(value);
                }}
                disabledDate={(current) => {
                  const fromDate = form.getFieldValue("ann_event_date_from");

                  return fromDate && current.isBefore(fromDate, "day");
                }}
              />
              )}
            </FormItem>

            <FormItem
              name="time_range"
              label="Event Time Duration"
              className="col-span-1"
              value={
                recordData?.ann_event_time_from &&
                recordData?.ann_event_time_to
                  ? `${dayjs(
                      recordData.ann_event_time_from,
                      ["HH:mm"]
                    ).format(TIME_FORMAT_12HM)} - ${dayjs(
                      recordData.ann_event_time_to,
                      ["HH:mm"]
                    ).format(TIME_FORMAT_12HM)}`
                  : "-"
              }
            >
              {isEditMode && (
              <RangePicker
                picker="time"
                format={TIME_FORMAT_12HM}
                className="input-field"
                style={{ width: "100%" }}
                allowClear
              />
              )}
            </FormItem>
            
            <FormItem
              label="Additional Information"
              className="col-span-3"
              value={
                <span>
                  {recordData?.ann_additional_info_label && (
                    <label className="text-base label-value title-text block">
                      {recordData.ann_additional_info_label}
                    </label>
                  )}

                  {recordData?.ann_additional_info && (
                    <p className="text-base label-value title-text">
                      {recordData.ann_additional_info}
                    </p>
                  )}
                </span>
              }
            >
              {isEditMode && (
              <div className="rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden">
                <Form.Item
                  name="ann_additional_info_label"
                  noStyle
                >
                  
                  <Input
                    placeholder="Header"
                    className="!rounded-none !border-0 !border-b input-field"
                  />
                
                </Form.Item>
                <Form.Item
                  name="ann_additional_info"
                  noStyle
                >
                  <Input.TextArea
                    placeholder="Message"
                    className="!rounded-none !border-0 input-field"
                    maxLength={1000}
                    rows={3}
                  />
                </Form.Item>
              </div>
              )}
            </FormItem>

            <FormItem
              name="ann_to_popup"
              label="Auto-Popup"
              className="col-span-1"
              value={recordData?.ann_to_popup == 1 ? "Yes" : "No"}
            >
              {isEditMode && (
              <SwitchField
										id="ann_to_popup"
										mode="edit"
										type="text"
										labelYes="Yes"
										labelNo="No"
									/>
              )}
            </FormItem>

            <FormItem
              name="ann_challenge_link"
              label="Challenge Link"
              className="col-span-1"
              value={recordData?.ann_challenge_link}
            >
              {isEditMode && (
              <Input
                  id="ann_challenge_link"
                  placeholder="Enter challenge link"
                  className="input-field"
                  maxLength={100}
                />
              )}
            </FormItem>

            {/* <FormItem
              name="ann_photo_id"
              label="Photo"
              className="col-span-1"
              value={recordData?.ann_photo_id}
            >
              {isEditMode && (
              
              )}
            </FormItem> */}
          </div>

        <div className="w-full flex justify-end mt-4">
            <CrudButtons
              mode={mode}
              recordId={recordData?.ann_id ?? 0}
              baseRoute="/console/announcements"
              cancelLabel="Cancel"
              submitLabel={submitLabel}
              moduleId={MODULE_ANNOUNCEMENTS}
            />
        </div>
      </Form>
  </Card>
  
  </>
  );
};

export default AnnouncementForm;